<template>
    <div class="related-link-editor">
        <h3 class="related-link-editor__title">
            {{ link ? 'Edit link' : 'Add link' }}
        </h3>
        <form class="related-link-editor__form" @submit.prevent="save">
            <div class="form-group">
                <label>Link text</label>
                <input v-model.trim="form.Text" type="text" class="form-control" required>
            </div>

            <div class="form-group">
                <label>Link type</label>

                <div class="related-link-editor__types">
                    <div class="form-check">
                        <input :id="`${editorId}-url`"
                               v-model="form.Type"
                               :value="LINK_TYPE_URL"
                               type="radio"
                               class="form-check-input">

                        <label class="form-check-label" :for="`${editorId}-url`">
                            Url
                        </label>
                    </div>

                    <div class="form-check">
                        <input :id="`${editorId}-file`"
                               v-model="form.Type"
                               :value="LINK_TYPE_FILE"
                               type="radio"
                               class="form-check-input">

                        <label class="form-check-label" :for="`${editorId}-file`">
                            Document
                        </label>
                    </div>
                </div>
            </div>

            <div v-if="form.Type === LINK_TYPE_URL" class="form-group">
                <label>URL</label>
                <input v-model.trim="form.Url" type="text" class="form-control" required>
            </div>

            <div v-else class="form-group">
                <label>Document</label>

                <div v-if="existingFile && !pendingFile" class="mb-2">
                    <div>
                        Current file:
                        <a :href="existingFile.Url" target="_blank" rel="noopener">
                            {{ existingFile.FileName }}
                        </a>
                    </div>
                </div>

                <RelatedLinkFileUpload ref="fileUpload"
                                       :container-name="uploadFolder"
                                       :folder-path="fileUploadOptions.folderPath"
                                       :cdn-path="fileUploadOptions.cdnPath"
                                       :put-block-path="fileUploadOptions.putBlockPath"
                                       :commit-path="fileUploadOptions.commitPath"
                                       :blob-exists-path="fileUploadOptions.blobExistsPath"
                                       :file-chunk-size="fileUploadOptions.fileChunkSize"
                                       :maximum-file-size-allowed="fileUploadOptions.maximumFileSizeAllowed"
                                       :file-types-allowed="fileUploadOptions.fileTypesAllowed"
                                       :allow-overwrite="false"
                                       :current-file="existingFile"
                                       @file-selected="onFileSelected"
                                       @cleared="onFileCleared"
                                       @use-existing-url="onUseExistingUrl"/>
            </div>

            <div class="form-group">
                <label>Description</label>
                <textarea v-model.trim="form.Description" class="form-control" rows="3" />
            </div>

            <div class="form-check">
                <input :id="`${editorId}-external`"
                       v-model="form.OpenInNewWindow"
                       type="checkbox"
                       class="form-check-input">

                <label class="form-check-label" :for="`${editorId}-external`">
                    Open in a new window
                </label>
            </div>

            <div class="related-link-editor__dates">
                <div class="form-group">
                    <label>Publish from</label>
                    <input v-model="form.PublishFrom" type="datetime-local" class="form-control">
                </div>

                <div class="form-group">
                    <label>Publish until</label>
                    <input v-model="form.PublishUntil" type="datetime-local" class="form-control">
                </div>
            </div>

            <div v-if="validationError" class="alert alert-danger">
                {{ validationError }}
            </div>

            <div class="related-link-editor__actions">
                <button type="button" class="btn btn-link" :disabled="isSaving" @click="$emit('cancel')">
                    Cancel
                </button>

                <button type="submit" class="btn btn-primary" :disabled="isSaving">
                    {{ isSaving ? 'Saving…' : 'Save' }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
    import { computed, reactive, ref } from 'vue';
    import RelatedLinkFileUpload from './RelatedLinkFileUpload.vue';
    import { joinUrl, requestJson } from './helpers';

    const LINK_TYPE_URL = 0;
    const LINK_TYPE_FILE = 1;

    const props = defineProps({
        link: {
            type: Object,
            default: null
        },

        saving: {
            type: Boolean,
            default: false
        },

        pageId: {
            type: String,
            required: true
        },

        relatedLinksBaseUrl: {
            type: String,
            required: true
        },

        fileUploadOptions: {
            type: Object,
            required: true
        },

        uploadFolder: {
            type: String,
            default: 'pdfs'
        }

    });

    const emit = defineEmits([
        'save',
        'cancel'
    ]);

    const fileUpload = ref(null);
    const pendingFile = ref(null);
    const registeringFile = ref(false);
    const validationError = ref('');

    const editorId = computed(() => {
        return props.link?.Id
            ? `related-link-${props.link.Id}`
            : 'related-link-new';
    });

    const existingFile = computed(() => props.link?.File ?? null);

    const isSaving = computed(() => {
        return props.saving || registeringFile.value;
    });

    const form = reactive({
        Text: props.link?.Text ?? '',
        Description: props.link?.Description ?? '',
        Type: props.link?.Type ?? LINK_TYPE_URL,
        Url: props.link?.Url ?? '',
        FileAssetId: props.link?.FileAssetId ?? null,
        OpenInNewWindow: props.link?.OpenInNewWindow ?? false,
        PublishFrom: toDateTimeInput(props.link?.PublishFrom),
        PublishUntil: toDateTimeInput(props.link?.PublishUntil)
    });

    function onUseExistingUrl(file) {
        validationError.value = '';
        pendingFile.value = null;

        form.Type = LINK_TYPE_URL;
        form.Url = file.Url;
        form.FileAssetId = null;
    }

    function toDateTimeInput(value) {
        if (!value) return '';

        return value.toString().substring(0, 16);
    }

    function onFileSelected(file) {        
        validationError.value = '';
        pendingFile.value = file;
    }

    function onFileCleared() {
        pendingFile.value = null;
        validationError.value = '';
    }

    async function save() {
        validationError.value = '';

        if (!form.Text) {
            validationError.value = 'Link text is required.';
            return;
        }

        if (form.Type === LINK_TYPE_URL && !form.Url) {
            validationError.value = 'URL is required.';
            return;
        }

        if (form.Type === LINK_TYPE_FILE && !form.FileAssetId && !pendingFile.value) {
            validationError.value = 'Choose a document.';
            return;
        }

        if (form.PublishFrom && form.PublishUntil && form.PublishUntil < form.PublishFrom) {
            validationError.value = 'Publish until must be after publish from.';
            return;
        }

        try {
            let fileAssetId = form.FileAssetId;
            let url = form.Url;

            if (form.Type === LINK_TYPE_FILE && pendingFile.value) {
                registeringFile.value = true;

                const uploaded = await fileUpload.value.upload();

                const fileAssetModel = {
                    BlobName: uploaded.BlobName,
                    FileName: uploaded.FileName,
                    Url: uploaded.Url,
                    ContentType: uploaded.ContentType,
                    Size: uploaded.Size
                };

                let fileAsset;

                if (uploaded.FileAssetId) {
                    fileAsset = await requestJson(
                        joinUrl(props.relatedLinksBaseUrl, `files/${uploaded.FileAssetId}`),
                        {
                            method: 'PUT',
                            body: fileAssetModel
                        }
                    );
                } else {
                    fileAsset = await requestJson(
                        joinUrl(props.relatedLinksBaseUrl, 'files'),
                        {
                            method: 'POST',
                            body: {
                                ...fileAssetModel,
                                OwnerPageId: props.pageId
                            }
                        }
                    );
                }

                fileAssetId = fileAsset.Id;
                url = fileAsset.Url;
            }

            if (form.Type === LINK_TYPE_URL) {
                fileAssetId = null;
            }

            emit('save', {
                Text: form.Text,
                Description: form.Description,
                Type: form.Type,
                Url: url,
                FileAssetId: fileAssetId,
                OpenInNewWindow: form.OpenInNewWindow,
                PublishFrom: form.PublishFrom || null,
                PublishUntil: form.PublishUntil || null
            });
        } catch (error) {
            validationError.value = error?.message || 'Unable to upload the document.';
        } finally {
            registeringFile.value = false;
        }
    }
</script>